import type {
  BenchJobState,
  BenchStepName,
  BenchStepStatus,
  ClubStatus,
  InterventionItemRead,
  InterventionState,
  MaintenanceAction,
  MaintenanceStatus,
  PartOutcome,
  KitCondition,
  KitReason,
  PlayerPosition,
  RegistrationRequestStatus,
  UserSubscription,
} from '@volee/api-client'

import type {
  AgeGroup,
  AppointmentRole,
  GuardianRelationship,
  KitReasonText,
  LeagueTier,
  MatchAnalysisState,
  MatchLeg,
  MatchStatus,
  PlayerFoot,
  RecordingStatus,
  SegmentQuality,
  Sex,
  StaffRole,
  TeamGender,
  TeamSheetState,
} from '../en/domain.ts'

// Italian labels. FIGC categories look untranslated because the value IS the Italian name.

export const IT_APPOINTMENT_ROLES: Record<AppointmentRole, string> = {
  official: 'Dirigente di società',
  team_official: 'Dirigente di squadra',
  coach: 'Allenatore',
}

export const IT_PLAYER_POSITIONS: Record<PlayerPosition, string> = {
  goalkeeper: 'Portiere',
  defender: 'Difensore',
  midfielder: 'Centrocampista',
  forward: 'Attaccante',
}

export const IT_PLAYER_FEET: Record<PlayerFoot, string> = {
  left: 'Sinistro',
  right: 'Destro',
  both: 'Ambidestro',
}

export const IT_AGE_GROUPS: Record<AgeGroup, string> = {
  seniores: 'Seniores',
  juniores: 'Juniores',
  allievi: 'Allievi',
  giovanissimi: 'Giovanissimi',
  esordienti: 'Esordienti',
  pulcini: 'Pulcini',
}

export const IT_TEAM_GENDERS: Record<TeamGender, string> = {
  male: 'Maschile',
  female: 'Femminile',
  open: 'Mista',
}

export const IT_SEXES: Record<Sex, string> = {
  male: 'Maschio',
  female: 'Femmina',
}

export const IT_CLUB_STATUSES: Record<ClubStatus, string> = {
  lead: 'Lead',
  in_review: 'In valutazione',
  active: 'Attiva',
  suspended: 'Sospesa',
  archived: 'Archiviata',
}

export const IT_REGISTRATION_REQUEST_STATUSES: Record<RegistrationRequestStatus, string> = {
  submitted: 'In attesa',
  needs_integration: 'Serve un’integrazione',
  approved: 'Approvata',
  rejected: 'Respinta',
}

export const IT_STAFF_ROLES: Record<StaffRole, string> = {
  admin: 'Amministratore',
  operations: 'Operazioni',
  support: 'Supporto',
}

export const IT_LEAGUE_TIERS: Record<LeagueTier, string> = {
  provincial: 'Provinciale',
  interprovincial: 'Interprovinciale',
  regional: 'Regionale',
  elite: 'Élite',
}

/* The bulletin's own adjective, agreeing in number with the category it describes —
 * "Provinciali", not "Provinciale": a girone's name reads as a plural noun phrase. */
export const IT_LEAGUE_TIER_ADJECTIVES: Record<LeagueTier, string> = {
  provincial: 'Provinciali',
  interprovincial: 'Interprovinciali',
  regional: 'Regionali',
  elite: 'Élite',
}

export const IT_GUARDIAN_RELATIONSHIPS: Record<GuardianRelationship, string> = {
  mother: 'Madre',
  father: 'Padre',
  guardian: 'Tutore',
}

export const IT_UNKNOWN_VALUE = 'Non specificato'

export const IT_MATCH_STATUSES: Record<MatchStatus, string> = {
  scheduled: 'Programmata',
  played: 'Giocata',
  postponed: 'Rinviata',
}

/* «Andata» e «Ritorno» sono i nomi del bollettino; per il terzo e il quarto giro non ce
 * n'è uno stampato, quindi si numera il turno invece di inventare una parola. */
/* «In arrivo» e non «in corso»: la registrazione è finita da ore, è il caricamento che
 * procede, e su SIM è la condizione normale di una domenica sera. */
export const IT_RECORDING_STATUSES: Record<RecordingStatus, string> = {
  scheduled: 'Programmata',
  in_progress: 'In arrivo',
  complete: 'Completa',
  incomplete: 'Incompleta',
  failed: 'Fallita',
  processed: 'Elaborata',
}

export const IT_TEAM_SHEET_STATES: Record<TeamSheetState, string> = {
  none: 'Nessuna convocazione',
  draft: 'Bozza',
  published: 'Pubblicata',
}

export const IT_MATCH_ANALYSIS_STATES: Record<MatchAnalysisState, string> = {
  not_submitted: 'Non inviata',
  submitted: 'Inviata',
  accepted: 'Accettata',
  rejected: 'Scartata',
  completed: 'Analizzata',
  failed: 'Non riuscita',
  imported: 'Risultati arrivati',
}

/* I due flussi della telecamera, detti per quello che valgono a chi legge. */
export const IT_SEGMENT_QUALITIES: Record<SegmentQuality, string> = {
  main: 'Alta risoluzione',
  sub: 'Flusso leggero',
}

export const IT_MATCH_LEGS: Record<MatchLeg, string> = {
  first: 'Andata',
  second: 'Ritorno',
  third: 'Terzo turno',
  fourth: 'Quarto turno',
}

export const IT_BENCH_JOB_STATES: Record<BenchJobState, string> = {
  offered: 'In attesa della scatola',
  accepted: 'In preparazione',
  saved: 'In preparazione',
  done: 'Pronta',
  failed: 'Ferma su un errore',
  taken_elsewhere: 'Presa da un altro ambiente',
}

export const IT_BENCH_STEPS: Record<BenchStepName, string> = {
  router: 'Router: password, SIM e Wi-Fi',
  camera: 'Camera: password e video',
  pi: 'Gateway: nome, password e Tailscale',
  cloud: 'Collegamento al cloud',
}

export const IT_BENCH_STEP_STATUSES: Record<BenchStepStatus, string> = {
  running: 'In corso',
  ok: 'Fatto',
  failed: 'Non riuscito',
}

export const IT_MAINTENANCE_ACTIONS: Record<MaintenanceAction, string> = {
  router_wifi_off: 'Spegni il Wi-Fi del router',
  camera_dhcp_reservation: 'Rifai la prenotazione della camera',
  camera_rtsp_on: 'Riaccendi RTSP',
  camera_reboot: 'Riavvia la camera',
  camera_apply_profile: 'Riapplica l’assetto della camera',
  read_inventory: 'Rileggi router e camera',
  adopt_router: 'Accogli il router nuovo',
  adopt_camera: 'Accogli la camera nuova',
}

export const IT_MAINTENANCE_STATUSES: Record<MaintenanceStatus, string> = {
  requested: 'In attesa',
  running: 'In corso',
  ok: 'Fatto',
  failed: 'Non riuscito',
  declined: 'Rifiutato',
  expired: 'Scaduto',
}

export const IT_KIT_CONDITIONS: Record<KitCondition, string> = {
  never_seen: 'Mai visto',
  silent: 'Muto',
  check: 'Da controllare',
  ok: 'Funziona',
}

export const IT_KIT_REASONS: Record<KitReason, KitReasonText> = {
  no_cameras: {
    title: 'Nessuna telecamera',
    hint: 'Il kit non registra niente: aggiungi la telecamera dell’armadietto.',
  },
  no_main_stream: {
    title: 'Una telecamera senza stream principale',
    hint: 'Senza lo stream principale la partita si registra e non si carica.',
  },
  stream_stuck: {
    title: 'Uno stream non produce',
    hint: 'Si riavvia senza produrre video: controlla che l’RTSP della telecamera sia acceso.',
  },
  clock_unsynced: {
    title: 'Orologio non sincronizzato',
    hint: 'Le registrazioni potrebbero partire all’ora sbagliata.',
  },
}

export const IT_SUBSCRIPTION_STATUSES: Record<UserSubscription['status'], string> = {
  active: 'Attivo',
  trialing: 'In prova',
  past_due: 'Pagamento in ritardo',
  paused: 'In pausa',
  incomplete: 'In attesa di pagamento',
  incomplete_expired: 'Terminato',
  canceled: 'Terminato',
  unpaid: 'Terminato',
}

export const IT_INTERVENTION_STATES: Record<InterventionState, string> = {
  open: 'Aperto',
  in_progress: 'In corso',
  closed: 'Chiuso',
  cancelled: 'Annullato',
}

export const IT_PART_OUTCOMES: Record<PartOutcome, string> = {
  reusable: 'Riusabile',
  broken: 'Guasto',
  disposed: 'Smaltito',
}

export const IT_INTERVENTION_PARTS: Record<InterventionItemRead['part'], string> = {
  gateway: 'Gateway',
  router: 'Router',
  sim: 'SIM',
  camera: 'Camera',
}
